import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import type { LocalIssue } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, Modal, Spinner, StatusBadge } from '../../ui/components';

/**
 * REPORTED -> ACKNOWLEDGED -> (REFERRED [M20_REFERRAL only] | IN_PROGRESS
 * [DISTRICT_LEVEL only]) -> RESOLVED -> CLOSED, with REJECTED reachable
 * from REPORTED/ACKNOWLEDGED — see m41-local-government's prisma/schema.prisma
 * enum comment and src/routes/localIssues.ts. The two mid-flow actions
 * (refer vs start-progress) are mutually exclusive and gated by the
 * issue's own routeTarget, computed once at report time from its category.
 */
export function IssueDetail({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const issueQ = useApiQuery<LocalIssue>(`/api/v1/m41/issues/${id}`);
  const issue = issueQ.data;

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showAssign, setShowAssign] = useState(false);
  const [assignedTo, setAssignedTo] = useState('');
  const [showRefer, setShowRefer] = useState(false);
  const [referNote, setReferNote] = useState('');
  const [showResolve, setShowResolve] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const refresh = () => {
    issueQ.reload();
    onChanged();
  };

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = () => run(() => api.post(`/api/v1/m41/issues/${id}/acknowledge`));
  const assign = () =>
    run(async () => {
      await api.post(`/api/v1/m41/issues/${id}/assign`, { assignedTo: assignedTo.trim() });
      setShowAssign(false);
    });
  const refer = () =>
    run(async () => {
      await api.post(`/api/v1/m41/issues/${id}/refer`, { note: referNote.trim() || undefined });
      setShowRefer(false);
      setReferNote('');
    });
  const startProgress = () => run(() => api.post(`/api/v1/m41/issues/${id}/start-progress`));
  const resolve = () =>
    run(async () => {
      await api.post(`/api/v1/m41/issues/${id}/resolve`, { notes: resolveNotes.trim() || undefined });
      setShowResolve(false);
      setResolveNotes('');
    });
  const closeIssue = () => run(() => api.post(`/api/v1/m41/issues/${id}/close`));
  const reject = () =>
    run(async () => {
      await api.post(`/api/v1/m41/issues/${id}/reject`, { reason: rejectReason.trim() });
      setShowReject(false);
      setRejectReason('');
    });

  return (
    <Modal title={issue ? issue.refNo : 'Local issue'} onClose={onClose} wide>
      {issueQ.loading ? <Spinner /> : null}
      {issueQ.error ? <ErrorNote message={issueQ.error} /> : null}
      {actionError ? (
        <div className="mb-3">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {issue ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={issue.status} />
            <StatusBadge status={issue.routeTarget} />
            <span className="text-xs text-slate-500">{issue.category.replaceAll('_', ' ')}</span>
            <span className="text-xs text-slate-400">· reported {formatDateTime(issue.createdAt)}</span>
          </div>
          <p className="text-sm text-slate-700">{issue.description}</p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-slate-500">Location</dt>
              <dd className="font-medium text-slate-800">{issue.locationDescription ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">GPS</dt>
              <dd className="font-medium text-slate-800">{issue.gpsLat != null && issue.gpsLng != null ? `${issue.gpsLat}, ${issue.gpsLng}` : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Reporter contact</dt>
              <dd className="font-medium text-slate-800">{issue.reporterContact ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Assigned to</dt>
              <dd className="font-medium text-slate-800">{issue.assignedTo ?? '—'}</dd>
            </div>
            {issue.m20ReferralRef ? (
              <div>
                <dt className="text-xs text-slate-500">M20 reference</dt>
                <dd className="font-medium text-slate-800">{issue.m20ReferralRef}</dd>
              </div>
            ) : null}
            {issue.resolutionNotes ? (
              <div className="sm:col-span-3">
                <dt className="text-xs text-slate-500">Resolution notes</dt>
                <dd className="font-medium text-slate-800">{issue.resolutionNotes}</dd>
              </div>
            ) : null}
          </dl>

          {issue.status === 'REJECTED' && issue.rejectionReason ? <ErrorNote message={`Rejected: ${issue.rejectionReason}`} /> : null}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {issue.status === 'REPORTED' ? (
              <>
                <button className="btn-primary" disabled={busy} onClick={acknowledge}>
                  Acknowledge
                </button>
                <button className="btn-danger" disabled={busy} onClick={() => setShowReject(true)}>
                  Reject
                </button>
              </>
            ) : null}
            {issue.status === 'ACKNOWLEDGED' ? (
              <>
                <button className="btn-secondary" disabled={busy} onClick={() => setShowAssign(true)}>
                  {issue.assignedTo ? 'Reassign' : 'Assign officer…'}
                </button>
                {issue.routeTarget === 'M20_REFERRAL' ? (
                  <button className="btn-primary" disabled={busy} onClick={() => setShowRefer(true)}>
                    Refer to M20…
                  </button>
                ) : (
                  <button className="btn-primary" disabled={busy} onClick={startProgress}>
                    Start progress
                  </button>
                )}
                <button className="btn-danger" disabled={busy} onClick={() => setShowReject(true)}>
                  Reject
                </button>
              </>
            ) : null}
            {issue.status === 'REFERRED' || issue.status === 'IN_PROGRESS' ? (
              <button className="btn-success" disabled={busy} onClick={() => setShowResolve(true)}>
                Resolve…
              </button>
            ) : null}
            {issue.status === 'RESOLVED' ? (
              <button className="btn-primary" disabled={busy} onClick={closeIssue}>
                Close
              </button>
            ) : null}
          </div>

          {showAssign ? (
            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <Field label="Assign to (officer name or id)">
                <input className="field-input" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} />
              </Field>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setShowAssign(false)}>
                  Back
                </button>
                <button className="btn-primary" disabled={busy || assignedTo.trim().length < 1} onClick={assign}>
                  Assign
                </button>
              </div>
            </div>
          ) : null}
          {showRefer ? (
            <div className="space-y-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
              <Field label="Note to M20 Infrastructure (optional)">
                <textarea className="field-input" rows={2} value={referNote} onChange={(e) => setReferNote(e.target.value)} />
              </Field>
              <p className="text-xs text-slate-500">
                No @onegov/core SDK client exposes M20's defect queue yet — this records the referral locally
                (m20ReferralRef) rather than actually forwarding it.
              </p>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setShowRefer(false)}>
                  Back
                </button>
                <button className="btn-primary" disabled={busy} onClick={refer}>
                  Confirm referral
                </button>
              </div>
            </div>
          ) : null}
          {showResolve ? (
            <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <Field label="Resolution notes (optional)">
                <textarea className="field-input" rows={2} value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)} />
              </Field>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setShowResolve(false)}>
                  Back
                </button>
                <button className="btn-success" disabled={busy} onClick={resolve}>
                  Confirm resolved
                </button>
              </div>
            </div>
          ) : null}
          {showReject ? (
            <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
              <Field label="Rejection reason">
                <textarea className="field-input" rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
              </Field>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setShowReject(false)}>
                  Back
                </button>
                <button className="btn-danger" disabled={busy || rejectReason.trim().length < 3} onClick={reject}>
                  Confirm reject
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
